import React from 'react';
import { DATA_LAYERS } from '../../../lib/dataLayers';
import { API_CONFIG } from '../../../lib/apiConfig';
import { LayerHeader } from './LayerHeader';
import { AboutLayer } from './AboutLayer';

interface CanopyHeightContentProps {
  state: string;
}

export const CanopyHeightContent: React.FC<CanopyHeightContentProps> = ({ state }) => {
  const layer = DATA_LAYERS.canopy_height;
  const hasDataset = Boolean(API_CONFIG.earthscale.canopyHeight[state]);

  return (
    <div className="space-y-6">
      <LayerHeader layer={layer} />

      <AboutLayer>
        <p>
          Canopy height is the vertical distance from the ground to the top of
          the forest canopy, measured per pixel from satellite-derived
          elevation models. Tall canopies generally indicate older or more
          productive stands; short canopies mark recent harvests, regeneration,
          or open ground.
        </p>
        <p>
          Tiles cover all four states in the proof of concept &mdash; California
          (2022), Oregon (2022), Alabama (2023), and Georgia (2023) &mdash; at
          5&thinsp;m resolution, served by Earthscale. The legend range is
          tuned per state so the color ramp spans each region&rsquo;s full
          canopy-height distribution; the shed boundary is the visual cue for
          what&rsquo;s in-scope (this layer renders across the dataset&rsquo;s
          full extent rather than being clipped to the shed).
        </p>
        <p>
          Per-shed summary statistics (mean, distribution, percentiles) are
          not yet published in this build; the panel surfaces the raster for
          visual inspection while that pipeline lands.
        </p>
      </AboutLayer>

      {hasDataset ? (
        <div className="bg-figma-card rounded-figma-panel p-4 border border-figma-lime/30 shadow-figma-card">
          <div className="text-xs font-roboto-mono text-green-400/70 mb-2">
            Live raster from Earthscale
          </div>
          <p className="text-sm font-roboto text-neutral-300 leading-relaxed">
            The canopy-height raster is rendering on the map. Supply-shed
            summary stats coming soon.
          </p>
        </div>
      ) : (
        <div className="bg-figma-card rounded-figma-panel p-4 border border-yellow-500/30 shadow-figma-card">
          <p className="text-sm font-roboto font-semibold text-yellow-400">
            Not yet available for {state}
          </p>
          <p className="text-xs font-roboto text-neutral-400 mt-1 leading-relaxed">
            Canopy-height coverage is configured for California, Oregon,
            Alabama, and Georgia. The Earthscale dataset for {state} is not
            yet wired up.
          </p>
        </div>
      )}
    </div>
  );
};
